// removed unused imports: useTTS, EditableTextBlock, ZoomableImage
import UnitPageShell from '../../components/shared/UnitPageShell';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { tokens } from '../../theme/tokens';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Tooltip,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import { useAuth } from '../../context/AuthContext';
import { apiConfig } from '../../utils/apiConfig';

type RawUser = Record<string, any> & {
  id?: string | number;
  email?: string;
  alias?: string;
  active?: boolean | number | string | null;
  deleted?: number | string | null;
};

type RankedUser = {
  id: string;
  email: string;
  alias: string;
  totalPoints: number;
  jokerCount: number;
  quizEntries: QuizScoreEntry[];
  teamId?: number | null;
  teamName?: string | null;
};

type HighscoreEntry = {
  id: string;
  alias: string;
  rank: number;
  totalPoints: number;
};

type MyScore = {
  alias: string;
  totalPoints: number;
  rank: number | null;
};

type QuizScoreEntry = {
  key: string;
  points: number;
};

type AllQuizzesData = {
  quizId: string;
  title: string;
  avgScore: number;
};

type TeamOption = {
  id: number;
  name: string;
  is_active?: number | boolean;
};

const isTeamActive = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') return value === '1' || value.toLowerCase() === 'true';
  return false;
};

// Alle verfügbaren Quizze aus der Datenbank (mit Themenbezug)
const ALL_QUIZZES = [
  { id: 'quiz_1_1', title: 'Lueftung – Zuluftregelung' },
  { id: 'quiz_1_2', title: 'Lueftung – Anlagenkomponenten' },
  { id: 'quiz_1_3', title: 'Lueftung – Sicherheit & Normen' },
  { id: 'quiz_1_4', title: 'Bauteile – Geraete & Bauteile' },
  { id: 'quiz_1_5', title: 'Bauteile – Pumpen' },
  { id: 'quiz_2_1', title: 'Heizung – Waermeerzeugung' },
  { id: 'quiz_2_2', title: 'Heizung – Heizkreise' },
  { id: 'quiz_2_3', title: 'Heizung – Verbraucher' },
  { id: 'quiz_2_4', title: 'Heizung – Hydraulische Schaltungen' },
  { id: 'quiz_2_5', title: 'Heizung – Wartung & Inbetriebnahme' },
  { id: 'quiz_3_1', title: 'Kaelte – Grundlagen Kaelte' },
  { id: 'quiz_3_2', title: 'Kaelte – Verteilung' },
  { id: 'quiz_3_3', title: 'Kaelte – Quiz & Statistik' },
  { id: 'quiz_4_1', title: 'Regelung – Temperaturregelung' },
  { id: 'quiz_4_2', title: 'Regelung – Luftregelung' },
  { id: 'quiz_4_3', title: 'Regelung – Beleuchtung & Praesenz' },
  { id: 'quiz_4_4', title: 'Regelung – Erweiterte Strategien' },
  { id: 'quiz_4_5', title: 'Bauteile – Mechanik & Montage' },
  { id: 'quiz_5_1', title: 'Bustechnik – Bus-Systeme' },
  { id: 'quiz_5_2', title: 'Bauteile – Frequenzumrichter & Ventilatoren' },
  { id: 'quiz_5_3', title: 'Bauteile – Schaltschrank' },
  { id: 'quiz_5_4', title: 'Bauteile – Netzteile & Elektronik' },
  { id: 'quiz_5_5', title: 'Bauteile – Sicherheit & Sensorik' },
  { id: 'quiz_5_99', title: 'Bustechnik – Abschluss' },
  { id: 'quiz_99', title: 'Abschlussquiz' },
  { id: 'quiz_6_1', title: 'Bauteile – Geraete & Bauteile' },
  { id: 'quiz_6_2', title: 'Bauteile – Pumpen' },
  { id: 'quiz_6_3', title: 'Bauteile – Frequenzumrichter & Ventilatoren' },
  { id: 'quiz_6_4', title: 'Bauteile – Schaltschrank' },
  { id: 'quiz_6_5', title: 'Bauteile – Netzteile & Elektronik' },
  { id: 'quiz_6_6', title: 'Bauteile – Sicherheit & Sensorik' },
  { id: 'quiz_6_7', title: 'Bauteile – Mechanik & Montage' }
];

const QUIZ_KEY_REGEX = /^quiz(_\d+)+$/i;

const getQuizKeysForUser = (user: RawUser): string[] => {
  const knownKeys = ALL_QUIZZES.map((quiz) => quiz.id);
  const dynamicKeys = Object.keys(user).filter((key) => QUIZ_KEY_REGEX.test(key));
  return Array.from(new Set([...knownKeys, ...dynamicKeys])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
};

const normalizeScore = (value: number): number => {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, Math.round(value)));
};

const getScoreColor = (score: number): string => {
  const normalizedScore = normalizeScore(score);
  if (normalizedScore === 0) return '#c62828'; // Error-Rot
  if (normalizedScore === 100) return '#2e7d32'; // Success-Grün

  const normalized = normalizedScore / 100;
  if (normalized < 0.5) {
    const ratio = normalized * 2;
    const r = Math.round(200 + (255 - 200) * ratio);
    const g = Math.round(40 + (193 - 40) * ratio);
    const b = Math.round(40 + (7 - 40) * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  }
  const ratio = (normalized - 0.5) * 2;
  const r = Math.round(255 + (46 - 255) * ratio);
  const g = Math.round(193 + (125 - 193) * ratio);
  const b = Math.round(7 + (50 - 7) * ratio);
  return `rgb(${r}, ${g}, ${b})`;
};

const parseScore = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return normalizeScore(value);
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? normalizeScore(parsed) : 0;
  }
  return 0;
};

const parseJokerCount = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, Math.floor(value));
  if (typeof value === 'string') {
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  }
  return 0;
};

const normalizeQuizKey = (key: string): string => key.trim().toLowerCase().replace(/[^a-z0-9]/g, '');

const getScoreForQuizKey = (user: RawUser, quizKey: string): number => {
  const direct = parseScore(user[quizKey]);
  if (direct > 0) return direct;

  const target = normalizeQuizKey(quizKey);
  for (const userKey of Object.keys(user)) {
    if (normalizeQuizKey(userKey) === target) {
      return parseScore(user[userKey]);
    }
  }
  return direct;
};

const isActiveUser = (user: RawUser): boolean => {
  const { active, deleted } = user;
  const deletedFlag = typeof deleted === 'string' ? parseInt(deleted, 10) : deleted;
  if (deletedFlag && deletedFlag !== 0) return false;
  if (typeof active === 'boolean') return active;
  if (typeof active === 'string') return active === '1' || active.toLowerCase() === 'true';
  if (typeof active === 'number') return active === 1;
  return true;
};

const computeTotalPoints = (user: RawUser): number => {
  return getQuizKeysForUser(user).reduce((sum, key) => sum + getScoreForQuizKey(user, key), 0);
};

const extractQuizEntries = (user: RawUser): QuizScoreEntry[] => {
  return getQuizKeysForUser(user).map((key) => ({
    key,
    points: getScoreForQuizKey(user, key)
  }));
};

const compareUsers = (a: RankedUser, b: RankedUser): number => {
  if (b.totalPoints !== a.totalPoints) return b.totalPoints - a.totalPoints;
  return a.id.localeCompare(b.id, undefined, { numeric: true, sensitivity: 'base' });
};

const Page: React.FC = () => {
  const { darkMode } = useTheme();
  const {
    token,
    user,
    canManageHighscore,
    canViewHighscorePublic,
    canViewHighscoreAggregate,
    canManageHighscorePrefs,
    canViewHighscoreAdminDetails,
    canManageHighscoreBars
  } = useAuth();

  const canManagePrefs = canManageHighscorePrefs || canManageHighscore;
  const canViewAdminDetails = canViewHighscoreAdminDetails || canManageHighscore;
  const canViewPublic = canViewHighscorePublic || canManageHighscore;
  const canViewAggregate = canViewHighscoreAggregate || canManageHighscore || canViewAdminDetails || canManagePrefs;
  const canAccessPage = canViewPublic || canViewAggregate || canViewAdminDetails || canManagePrefs;
  const isAdmin = canManagePrefs || canViewAdminDetails;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [topEntries, setTopEntries] = useState<HighscoreEntry[]>([]);
  const [myScore, setMyScore] = useState<MyScore | null>(null);
  const [rankedUsersState, setRankedUsersState] = useState<RankedUser[] | null>(null);
  const [publicAggregateData, setPublicAggregateData] = useState<AllQuizzesData[]>([]);
  const [excludedUserIds, setExcludedUserIds] = useState<Record<string, boolean>>({});
  const [titleOverrides, setTitleOverrides] = useState<Record<string, string>>({});
  const [disabledBars, setDisabledBars] = useState<Record<string, boolean>>({}); // Deaktivierte Balken
  const draftTitleOverridesRef = React.useRef<Record<string, string>>({});
  const draftDisabledBarsRef = React.useRef<Record<string, boolean>>({});
  const [legendVersion, setLegendVersion] = useState(0);
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [teamFilter, setTeamFilter] = useState('all');

  const currentId = user?.id ? String(user.id) : null;
  const currentEmail = user?.email ?? null;
  const selectedTeamName = useMemo(() => {
    if (teamFilter === 'all') return null;
    const match = teams.find((team) => String(team.id) === teamFilter);
    return match ? match.name : null;
  }, [teamFilter, teams]);

  const visibleTeams = useMemo(() => {
    return teams.filter((team) => isTeamActive(team.is_active));
  }, [teams]);

  const getDisplayTitle = useCallback(
    (quizId: string, fallback: string) => titleOverrides[quizId] ?? fallback,
    [titleOverrides]
  );

  const handleSavePrefs = useCallback(() => {
    if (!canManagePrefs && !canManageHighscoreBars) return;
    const nextTitleOverrides = { ...draftTitleOverridesRef.current };
    const nextDisabledBars = { ...draftDisabledBarsRef.current };
    setTitleOverrides(nextTitleOverrides);
    setDisabledBars(nextDisabledBars);
    if (!token) return;
    const payload = {
      action: 'updateHighscorePrefs',
      excludedUserIds,
      titleOverrides: nextTitleOverrides,
      disabledBars: nextDisabledBars
    };
    console.log('[Highscore] Manuelles Speichern - Payload:', payload);
    fetch(apiConfig.apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    })
      .then((response) => {
        if (!response.ok) {
          console.error('[Highscore] Speichern fehlgeschlagen:', response.status);
        } else {
          console.log('[Highscore] Speichern erfolgreich');
        }
        return response.json().catch(() => null);
      })
      .then((data) => {
        if (data?.success) {
          console.log('[Highscore] Server bestätigt Speichern');
        } else if (data?.error) {
          console.error('[Highscore] Server-Fehler:', data.error);
        }
      })
      .catch((err) => {
        console.error('[Highscore] Netzwerk-Fehler beim Speichern:', err);
      });
  }, [canManagePrefs, canManageHighscoreBars, excludedUserIds, token]);

  useEffect(() => {
    if ((!canManagePrefs && !canManageHighscoreBars) || !token || !prefsLoaded) return;
    const payload = {
      action: 'updateHighscorePrefs',
      excludedUserIds,
      titleOverrides,
      disabledBars
    };
    const timeout = setTimeout(() => {
      console.log('[Highscore] Auto-Save ausgelöst - Payload:', payload);
      fetch(apiConfig.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      })
        .then((response) => {
          if (!response.ok) {
            console.error('[Highscore] Auto-Save fehlgeschlagen:', response.status);
          } else {
            console.log('[Highscore] Auto-Save erfolgreich');
          }
          return response.json().catch(() => null);
        })
        .then((data) => {
          if (data?.success) {
            console.log('[Highscore] Server bestätigt Auto-Save');
          } else if (data?.error) {
            console.error('[Highscore] Server-Fehler bei Auto-Save:', data.error);
          }
        })
        .catch((err) => {
          console.error('[Highscore] Netzwerk-Fehler bei Auto-Save:', err);
        });
    }, 600);
    return () => clearTimeout(timeout);
  }, [excludedUserIds, titleOverrides, disabledBars, canManagePrefs, canManageHighscoreBars, token, prefsLoaded]);

  // Berechne Quiz-Durchschnitte für einen einzelnen User (in Prozent)
  const getUserQuizScores = useCallback((user: RankedUser): AllQuizzesData[] => {
    const scoreMap: Record<string, number> = {};
    user.quizEntries.forEach((q) => {
      scoreMap[q.key] = q.points;
    });
    
    return ALL_QUIZZES
      .filter((quiz) => !disabledBars[quiz.id])
      .map((quiz) => ({
      quizId: quiz.id,
      title: quiz.title,
      avgScore: scoreMap[quiz.id] || 0
    }));
  }, [disabledBars]);

  // Aggregate quiz totals for admin overview (alle User gemittelt)
  const aggregateQuizTotals = useCallback((ranked: RankedUser[]): AllQuizzesData[] => {
    const totals: Record<string, { sum: number; count: number }> = {};
    
    // Initialisiere alle Quizze
    ALL_QUIZZES.forEach((q) => {
      totals[q.id] = { sum: 0, count: 0 };
    });
    
    // Sammle Daten von allen Usern
    ranked.forEach((u) => {
      u.quizEntries.forEach((q) => {
        if (totals[q.key]) {
          totals[q.key].sum += q.points;
          totals[q.key].count += 1;
        }
      });
    });
    
    // Berechne Durchschnitte
    return ALL_QUIZZES.map((quiz) => {
      const data = totals[quiz.id];
      const avgScore = data.count > 0 ? data.sum / data.count : 0;
      return {
        quizId: quiz.id,
        title: quiz.title,
        avgScore: Math.round(avgScore)
      };
    });
  }, []);

  const filteredRankedUsers = useMemo(() => {
    if (!rankedUsersState) return null;
    const teamFiltered = teamFilter === 'all'
      ? rankedUsersState
      : rankedUsersState.filter((u) => String(u.teamId ?? '') === teamFilter);
    return teamFiltered.filter((u) => !excludedUserIds[u.id]);
  }, [excludedUserIds, rankedUsersState, teamFilter]);

  const aggregateData = useMemo(() => {
    if (!filteredRankedUsers) return [] as AllQuizzesData[];
    return aggregateQuizTotals(filteredRankedUsers);
  }, [aggregateQuizTotals, filteredRankedUsers]);

  const displayAggregateData = useMemo(() => {
    if (!canViewAggregate) return [] as AllQuizzesData[];
    if (isAdmin) return aggregateData;
    return publicAggregateData;
  }, [aggregateData, canViewAggregate, isAdmin, publicAggregateData]);

  // Filtere deaktivierte Balken aus
  const filteredDisplayAggregateData = useMemo(() => {
    return displayAggregateData.filter((item) => !disabledBars[item.quizId]);
  }, [displayAggregateData, disabledBars]);

  const fetchHighscores = useCallback(async () => {
    if (!token) {
      setError('Kein Token vorhanden. Bitte erneut einloggen.');
      setTopEntries([]);
      setMyScore(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      if (isAdmin) {
        const response = await fetch(`${apiConfig.apiUrl}?action=getAllUsers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || !Array.isArray(data)) {
          throw new Error(data?.error || `Serverfehler: ${response.status}`);
        }

        const rankedUsers: RankedUser[] = data
          .filter((item: RawUser) => isActiveUser(item))
          .map((item: RawUser) => ({
            id: String(item.id ?? ''),
            email: String(item.email ?? ''),
            alias: item.alias ? String(item.alias) : 'Unbekannt',
            totalPoints: computeTotalPoints(item),
            jokerCount: parseJokerCount((item as any).joker_count),
            quizEntries: extractQuizEntries(item),
            teamId: typeof (item as any).team_id === 'number' ? (item as any).team_id : (item as any).team_id ? Number((item as any).team_id) : null,
            teamName: (item as any).team_name ?? null
          }))
          .filter((item) => item.id || item.email)
          .sort(compareUsers);

        setRankedUsersState(rankedUsers);
      } else {
        const teamQuery = teamFilter !== 'all' ? `&teamId=${encodeURIComponent(teamFilter)}` : '';
        const response = await fetch(`${apiConfig.apiUrl}?action=getHighscorePublic${teamQuery}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json().catch(() => null);
        if (!response.ok || !data?.success) {
          throw new Error(data?.error || `Serverfehler: ${response.status}`);
        }
        setTopEntries(Array.isArray(data.topEntries) ? data.topEntries : []);
        setMyScore(data.myScore ?? null);
        setPublicAggregateData(Array.isArray(data.aggregateData) ? data.aggregateData : []);
        if (data.disabledBars && typeof data.disabledBars === 'object') {
          setDisabledBars(data.disabledBars);
          draftDisabledBarsRef.current = { ...data.disabledBars };
        }
        setRankedUsersState(null);
      }
    } catch (err: any) {
      setError(err?.message || 'Highscores konnten nicht geladen werden.');
      setTopEntries([]);
      setMyScore(null);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, teamFilter, token]);

  const teamFilteredUsers = useMemo(() => {
    if (!rankedUsersState) return null;
    if (teamFilter === 'all') return rankedUsersState;
    return rankedUsersState.filter((u) => String(u.teamId ?? '') === teamFilter);
  }, [rankedUsersState, teamFilter]);

  useEffect(() => {
    if (!isAdmin) return;
    if (!teamFilteredUsers) {
      setTopEntries([]);
      setMyScore(null);
      return;
    }
    const rankByScore = new Map<number, number>();
    teamFilteredUsers.forEach((entry, index) => {
      if (!rankByScore.has(entry.totalPoints)) {
        rankByScore.set(entry.totalPoints, index + 1);
      }
    });

    const topFive = teamFilteredUsers.slice(0, 5).map((entry) => ({
      id: entry.id,
      alias: entry.alias,
      totalPoints: entry.totalPoints,
      rank: rankByScore.get(entry.totalPoints) ?? 0
    }));

    const currentUserScore = teamFilteredUsers.find((entry) => {
      if (currentId && entry.id === currentId) return true;
      if (currentEmail && entry.email === currentEmail) return true;
      return false;
    });

    setTopEntries(topFive);
    setMyScore(
      currentUserScore
        ? {
            alias: currentUserScore.alias,
            totalPoints: currentUserScore.totalPoints,
            rank: rankByScore.get(currentUserScore.totalPoints) ?? null
          }
        : null
    );
  }, [currentEmail, currentId, isAdmin, teamFilteredUsers]);

  useEffect(() => {
    if (token && canAccessPage) {
      fetchHighscores();
    }
  }, [fetchHighscores, token, canAccessPage]);

  useEffect(() => {
    if (!token) return;
    const loadPrefs = async () => {
      try {
        const response = await fetch(`${apiConfig.apiUrl}?action=getHighscorePrefs`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json().catch(() => null);
        console.log('[Highscore] Prefs geladen:', data);
        if (response.ok && data?.success && data?.prefs) {
          if (data.prefs.excludedUserIds && typeof data.prefs.excludedUserIds === 'object') {
            setExcludedUserIds(data.prefs.excludedUserIds);
          }
          if (data.prefs.titleOverrides && typeof data.prefs.titleOverrides === 'object') {
            setTitleOverrides(data.prefs.titleOverrides);
            draftTitleOverridesRef.current = { ...data.prefs.titleOverrides };
          }
          if (data.prefs.disabledBars && typeof data.prefs.disabledBars === 'object') {
            console.log('[Highscore] disabledBars geladen:', data.prefs.disabledBars);
            setDisabledBars(data.prefs.disabledBars);
            draftDisabledBarsRef.current = { ...data.prefs.disabledBars };
          } else {
            console.log('[Highscore] Keine disabledBars gefunden, verwende leeres Objekt');
          }
        } else {
          console.error('[Highscore] Fehler beim Laden der Prefs:', data?.error || 'Unbekannter Fehler');
        }
      } catch (err) {
        console.error('[Highscore] Netzwerk-Fehler beim Laden der Prefs:', err);
      } finally {
        setPrefsLoaded(true);
      }
    };
    loadPrefs();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    const loadTeams = async () => {
      try {
        const response = await fetch(`${apiConfig.apiUrl}?action=getTeams`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await response.json().catch(() => null);
        if (response.ok && data?.success && Array.isArray(data.teams)) {
          setTeams(data.teams);
        }
      } catch {
        // ignore
      }
    };
    loadTeams();
  }, [token]);

  useEffect(() => {
    if (teamFilter === 'all' || visibleTeams.length === 0) return;
    const exists = visibleTeams.some((team) => String(team.id) === teamFilter);
    if (!exists) {
      setTeamFilter('all');
    }
  }, [teamFilter, visibleTeams]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('highscoreTeamFilter');
      if (stored) setTeamFilter(stored);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('highscoreTeamFilter', teamFilter);
    } catch {
      // ignore
    }
  }, [teamFilter]);

  useEffect(() => {
    draftTitleOverridesRef.current = { ...titleOverrides };
    setLegendVersion((prev) => prev + 1);
  }, [titleOverrides]);

  const emptyStateText = useMemo(() => {
    if (loading) return 'Highscores werden geladen…';
    if (error) return error;
    return 'Noch keine Highscores vorhanden.';
  }, [error, loading]);

  // Simple reusable SVG bar chart for stats
  const BarChartSVG: React.FC<{
    data: AllQuizzesData[];
    width?: number;
    height?: number;
    labelMode?: 'none' | 'index';
  }> = ({ data, width = 960, height = 300, labelMode = 'none' }) => {
    const marginBottom = 32;
    const margin = { top: 20, right: 20, bottom: marginBottom, left: 20 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;
    const barW = innerW / data.length;
    
    return (
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMin meet"
        role="img"
        aria-label="Quiz-Übersicht"
        style={{ width: '100%', height, display: 'block' }}
      >
        <rect x={0} y={0} width={width} height={height} fill={darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt} rx={8} />
        {data.map((d, i) => {
          const score = normalizeScore(d.avgScore);
          const h = Math.round((score / 100) * innerH);
          const x = margin.left + i * barW + 2;
          const y = margin.top + innerH - h;
          const barColor = getScoreColor(score);
          
          const displayTitle = getDisplayTitle(d.quizId, d.title);
          return (
            <g key={d.quizId}>
              <title>{`${displayTitle}: ${score}%`}</title>
              <rect 
                x={x} 
                y={y} 
                width={barW - 4} 
                height={Math.max(h, 2)} 
                fill={barColor} 
                rx={3}
              />
              <text
                x={x + (barW - 4) / 2}
                y={y - 5}
                textAnchor="middle"
                fontSize={10}
                fontWeight={600}
                fill={darkMode ? tokens.colors.dark.text : tokens.colors.light.custom1}
              >
                {score}%
              </text>
              {labelMode === 'index' && (
                <text
                  x={x + (barW - 4) / 2}
                  y={margin.top + innerH + 16}
                  textAnchor="middle"
                  fontSize={9}
                  fill={darkMode ? tokens.colors.dark.text : tokens.colors.light.custom1}
                >
                  {i + 1}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    );
  };

  const LegendItem: React.FC<{
    item: AllQuizzesData;
    index: number;
    editable: boolean;
    version: number;
  }> = React.memo(({ item, index, editable, version }) => {
    const [value, setValue] = useState(getDisplayTitle(item.quizId, item.title));

    useEffect(() => {
      setValue(getDisplayTitle(item.quizId, item.title));
    }, [item.quizId, item.title, version]);

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            minWidth: 18,
            textAlign: 'center',
            color: darkMode ? tokens.colors.dark.text : tokens.colors.light.custom1,
            fontWeight: 700
          }}
        >
          {index + 1}.
        </Typography>
        <Box sx={{ width: 14, height: 14, bgcolor: getScoreColor(item.avgScore), borderRadius: 1 }} />
        {editable ? (
          <TextField
            size="small"
            value={value}
            onChange={(event) => {
              const next = event.target.value;
              setValue(next);
              draftTitleOverridesRef.current = {
                ...draftTitleOverridesRef.current,
                [item.quizId]: next
              };
            }}
            inputProps={{ style: { fontWeight: 600, fontSize: 13 } }}
            sx={{ width: '100%', minWidth: 140 }}
          />
        ) : (
          <Typography
            variant="body2"
            noWrap
            title={getDisplayTitle(item.quizId, item.title)}
            sx={{ color: darkMode ? tokens.colors.dark.text : tokens.colors.light.custom1, fontWeight: 600, fontSize: 13 }}
          >
            {getDisplayTitle(item.quizId, item.title)}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: '#4F647A' }}>
          {normalizeScore(item.avgScore)}%
        </Typography>
      </Box>
    );
  });

  const ChartLegend: React.FC<{
    data: AllQuizzesData[];
    editable?: boolean;
  }> = ({ data, editable = false }) => (
    <Box sx={{ mt: 1 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
          gap: 0.5,
          maxHeight: { xs: 260, sm: 'none' },
          overflowY: { xs: 'auto', sm: 'visible' }
        }}
      >
        {data.map((item, index) => (
          <LegendItem key={item.quizId} item={item} index={index} editable={editable} version={legendVersion} />
        ))}
      </Box>
    </Box>
  );

  if (!token) {
    return (
      <UnitPageShell title="Highscores">
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt }}>
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Typography>Für die Highscores ist ein Login erforderlich.</Typography>
        </Paper>
      </Box>
      </UnitPageShell>
    );
  }

  if (!canAccessPage) {
    return (
      <UnitPageShell title="Zugriff verweigert">
        <Box sx={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Alert severity="error">Keine Berechtigung fuer die Highscores.</Alert>
        </Box>
      </UnitPageShell>
    );
  }

  return (
      <UnitPageShell title="Highscores" showTTS={false} actions={
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="team-filter-label">Teamfilter</InputLabel>
            <Select
              labelId="team-filter-label"
              label="Teamfilter"
              value={teamFilter}
              onChange={(event) => setTeamFilter(String(event.target.value))}
            >
              <MenuItem value="all">Alle Teams</MenuItem>
                          {visibleTeams.map((team) => (
                <MenuItem key={team.id} value={String(team.id)}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Aktualisieren">
            <span>
              <Button color="inherit" startIcon={<RefreshIcon />} onClick={fetchHighscores} disabled={loading}>
                Aktualisieren
              </Button>
            </span>
          </Tooltip>
          {(canManagePrefs || canManageHighscoreBars) && (
            <Tooltip title="Titel speichern">
              <span>
                <Button color="inherit" startIcon={<SaveIcon />} onClick={handleSavePrefs} disabled={loading}>
                  Speichern
                </Button>
              </span>
            </Tooltip>
          )}
        </Stack>
      }>
        <Stack spacing={3}>
          {error && !loading && <Alert severity="error">{error}</Alert>}

          {/* Mein Punktestand (für alle) */}
          {canViewPublic && (
            <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, boxShadow: '0 18px 40px rgba(13,53,89,0.12)' }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <EmojiEventsIcon sx={{ fontSize: 40, color: tokens.colors.primary }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: darkMode ? tokens.colors.dark.text : tokens.colors.light.custom1, fontWeight: 600 }}>
                    Mein Punktestand
                  </Typography>
                  {myScore ? (
                    <>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {myScore.totalPoints} Punkte
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4F647A' }}>
                        Rang: {myScore.rank ?? 'N/A'} — Alias: {myScore.alias}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: '#4F647A' }}>
                      {selectedTeamName ? 'Kein persoenlicher Score fuer dieses Team.' : 'Kein persoenlicher Score gefunden.'}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Paper>
          )}

          {/* Normale Ansicht: Top 5 */}
          {!isAdmin && canViewPublic && (
            <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, boxShadow: '0 22px 44px rgba(13,53,89,0.12)' }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: darkMode ? tokens.colors.dark.text : '#0D3559' }}>
                  Top 5 Spieler{selectedTeamName ? ` (${selectedTeamName})` : ''}
                </Typography>

                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                )}

                {!loading && !topEntries.length && (
                        <Typography sx={{ color: darkMode ? tokens.colors.dark.textSecondary : tokens.colors.light.custom2 }}>{emptyStateText}</Typography>
                )}

                {!loading && topEntries.length > 0 && (
                  <Stack spacing={1.5}>
                    {topEntries.map((entry) => (
                      <Box
                        key={entry.id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(13,53,89,0.05)',
                          borderRadius: 2,
                          px: 2.5,
                          py: 1.5
                        }}
                      >
                        <Typography sx={{ color: darkMode ? tokens.colors.dark.text : tokens.colors.light.custom1, fontWeight: 600 }}>
                          {entry.rank}.
                        </Typography>
                        <Typography sx={{ flex: 1, mx: 2, color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text, fontWeight: 600 }}>
                          {entry.alias}
                        </Typography>
                        <Typography sx={{ fontStyle: 'italic', color: darkMode ? tokens.colors.dark.textSecondary : tokens.colors.light.custom2 }}>
                          {entry.totalPoints} Punkte
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Paper>
          )}

          {/* Gesamtübersicht (für alle sichtbar) */}
          {canViewAggregate && (
            <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, boxShadow: '0 22px 44px rgba(13,53,89,0.12)' }}>
              <Stack spacing={3}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: darkMode ? tokens.colors.dark.text : '#0D3559' }}>
                  Quiz-Uebersicht (Durchschnittswerte {selectedTeamName ? `(${selectedTeamName})` : 'aller aktiven Nutzer'})
                </Typography>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {filteredDisplayAggregateData.length > 0 ? (
                      <Box sx={{ overflowX: 'auto', pb: 2, WebkitOverflowScrolling: 'touch' }}>
                        <BarChartSVG
                          data={filteredDisplayAggregateData}
                          width={960}
                          height={320}
                          labelMode="index"
                        />
                        <ChartLegend key={legendVersion} data={filteredDisplayAggregateData} editable={canManagePrefs} />
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ color: '#4F647A' }}>
                        Keine Quiz-Daten verfügbar.
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 20, height: 20, bgcolor: '#c62828', borderRadius: 1 }} />
                        <Typography variant="caption" sx={{ color: '#4F647A' }}>0%</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 20, height: 20, bgcolor: '#ffc107', borderRadius: 1 }} />
                        <Typography variant="caption" sx={{ color: '#4F647A' }}>50%</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 20, height: 20, bgcolor: '#2e7d32', borderRadius: 1 }} />
                        <Typography variant="caption" sx={{ color: '#4F647A' }}>100%</Typography>
                      </Box>
                    </Box>
                  </>
                )}
              </Stack>
            </Paper>
          )}

          {/* Admin-Ansicht: aufklappbare Nutzer */}
          {/* Admin-Ansicht: aufklappbare Nutzer */}
          {canViewAdminDetails && (
            <Stack spacing={3}>
              {/* Bar-Deaktivierungs-Panel */}
              {canManageHighscoreBars && (
                <Accordion
                  defaultExpanded
                  sx={{ borderRadius: 3, boxShadow: '0 22px 44px rgba(13,53,89,0.12)', bgcolor: darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: darkMode ? tokens.colors.dark.text : tokens.colors.light.custom1 }}>
                      🎯 Balken-Konfiguration
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <Typography variant="body2" sx={{ color: darkMode ? tokens.colors.dark.textSecondary : tokens.colors.light.custom2 }}>
                        Deaktiviere einzelne Quiz-Balken. Deaktivierte Balken werden Benutzern ausgeblendet, Positionen rutschen auf.
                      </Typography>
                      <Stack spacing={1} sx={{ maxHeight: 400, overflowY: 'auto' }}>
                        {displayAggregateData.map((quiz) => (
                          <FormControlLabel
                            key={quiz.quizId}
                            control={
                              <Checkbox
                                size="small"
                                checked={!disabledBars[quiz.quizId]}
                                onChange={(e) => {
                                  const isDisabled = !e.target.checked;
                                  console.log(`[Highscore] Balken ${quiz.quizId} ${isDisabled ? 'deaktiviert' : 'aktiviert'}`);
                                  // Ref immutable aktualisieren
                                  draftDisabledBarsRef.current = {
                                    ...draftDisabledBarsRef.current,
                                    [quiz.quizId]: isDisabled
                                  };
                                  setDisabledBars((prev) => ({
                                    ...prev,
                                    [quiz.quizId]: isDisabled
                                  }));
                                }}
                              />
                            }
                            label={
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {quiz.title} ({quiz.avgScore}%)
                              </Typography>
                            }
                          />
                        ))}
                      </Stack>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              <Paper sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, boxShadow: '0 22px 44px rgba(13,53,89,0.12)' }}>
                <Stack spacing={2}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: darkMode ? tokens.colors.dark.text : '#0D3559' }}>
                    Alle Nutzer (aufklappbar)
                  </Typography>
                  {teamFilteredUsers && teamFilteredUsers.length > 0 && (
                    <Typography variant="body2" sx={{ color: '#4F647A' }}>
                      Aus Gesamt-Auswertung ausgeschlossen: {Object.values(excludedUserIds).filter(Boolean).length}
                    </Typography>
                  )}
                  {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  )}
                  {!loading && teamFilteredUsers && teamFilteredUsers.length > 0 ? (
                    <Stack spacing={1.5}>
                      {teamFilteredUsers.map((u, idx) => (
                        <Accordion key={u.id || `${u.email}-${idx}`} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box
                              sx={{
                                display: 'grid',
                                alignItems: 'center',
                                gap: 1,
                                width: '100%',
                                gridTemplateColumns: {
                                  xs: '32px 1fr 90px',
                                  md: '32px 1.2fr 130px 200px 240px'
                                }
                              }}
                            >
                              <Typography sx={{ fontWeight: 700, color: darkMode ? tokens.colors.dark.text : '#0D3559' }}>
                                #{idx + 1}
                              </Typography>
                              <Typography sx={{ fontWeight: 600, color: darkMode ? tokens.colors.dark.text : tokens.colors.light.text }}>
                                {u.alias}
                              </Typography>
                        <Typography variant="caption" sx={{ color: darkMode ? tokens.colors.dark.textSecondary : tokens.colors.light.custom2 }}>
                          {u.totalPoints} Punkte · 🃏 {u.jokerCount}
                        </Typography>
                        <Typography sx={{ color: darkMode ? tokens.colors.dark.textSecondary : tokens.colors.light.custom2, fontStyle: 'italic', display: { xs: 'none', md: 'block' } }}>
                                {u.email}
                              </Typography>
                              <FormControlLabel
                                onClick={(event) => event.stopPropagation()}
                                onFocus={(event) => event.stopPropagation()}
                                control={
                                  <Checkbox
                                    checked={Boolean(excludedUserIds[u.id])}
                                    onClick={(event) => event.stopPropagation()}
                                    onChange={(e) =>
                                      setExcludedUserIds((prev) => ({
                                        ...prev,
                                        [u.id]: e.target.checked
                                      }))
                                    }
                                  />
                                }
                                label="Aus Gesamt-Auswertung"
                                sx={{ justifySelf: { xs: 'end', md: 'start' } }}
                              />
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={2} sx={{ width: '100%' }}>
                              <Typography variant="caption" sx={{ color: '#4F647A' }}>
                                Balken-Nummern siehe Gesamtuebersicht.
                              </Typography>
                              <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                                <BarChartSVG
                                  data={getUserQuizScores(u).filter((item) => !disabledBars[item.quizId])}
                                  width={960}
                                  height={320}
                                  labelMode="index"
                                />
                              </Box>
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    </Stack>
                  ) : (
                    !loading && (
                      <Typography variant="body2" sx={{ color: '#4F647A' }}>
                        Keine Nutzerdaten verfügbar.
                      </Typography>
                    )
                  )}
                </Stack>
              </Paper>
            </Stack>
          )}
        </Stack>
    </UnitPageShell>
  );
};

export default Page;
